import api from 'api'
import { get } from 'utils/lodash'

import {
  CACHE_TAG_WORK_ORDER_LIST,
  CACHE_TAG_WORK_ORDER_DETAILS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_WORK_ORDER_NOTES_LIST,
  CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS,
  CACHE_TAG_WORK_ORDER_NOTE_VERSION_LIST,
} from '../cacheTagTypes'

import { optimisticNoteCreate } from './optimisticNoteCount'

type NoteRecord = Record<string, unknown>
type NotesResponse = { notes: NoteRecord[] }
type NoteBody = { note: string }

type WorkOrderUpdateArg = { id: string; noteId?: string; data: NoteBody }
type WorkOrderPatchArg = { id: string; noteId: string; data: NoteRecord }

const writeInvalidatesTags = (id: string) => [
  { type: CACHE_TAG_WORK_ORDER_NOTES_LIST, id },
  CACHE_TAG_WORK_ORDER_LIST,
  CACHE_TAG_WORK_ORDER_DETAILS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS,
  CACHE_TAG_WORK_ORDER_NOTE_VERSION_LIST,
]

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    getAllWorkOrderNotes: builder.query<NotesResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/api/v1/core/work-orders/${id}/notes`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [CACHE_TAG_WORK_ORDER_NOTES_LIST, { type: CACHE_TAG_WORK_ORDER_NOTES_LIST, id }],
    }),
    getWorkOrderNoteDetails: builder.query<NoteRecord, { workOrderId: string; noteId: string }>({
      query: ({ workOrderId, noteId }) => ({
        url: `/api/v1/core/work-orders/${workOrderId}/notes/${noteId}`,
        method: 'GET',
      }),
    }),
    getWorkOrderNoteHistory: builder.query<NotesResponse, { id: string; noteId: string }>({
      query: ({ id, noteId }) => ({
        url: `/api/v1/core/work-orders/${id}/notes/${noteId}/history`,
        method: 'GET',
      }),
    }),
    createWorkOrderNote: builder.mutation<NoteRecord, { id: string; data: NoteBody }>({
      query: ({ id, data }) => ({
        url: `/api/v1/core/work-orders/${id}/notes`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => writeInvalidatesTags(id),
      // Move the board badge now; the invalidation above reconciles it afterwards.
      onQueryStarted: optimisticNoteCreate('workOrderNoteCount', id => stop => get(stop, 'workOrderId', '') === id),
    }),
    updateWorkOrderNoteNew: builder.mutation<NoteRecord, WorkOrderUpdateArg>({
      query: ({ id, noteId, data }) => ({
        url: `/api/v1/core/work-orders/${id}/notes/${noteId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => writeInvalidatesTags(id),
    }),
    deleteWorkOrderNote: builder.mutation<void, { id: string; noteId: string }>({
      query: ({ id, noteId }) => ({
        url: `/api/v1/core/work-orders/${id}/notes/${noteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => writeInvalidatesTags(id),
    }),
    patchWorkOrderNote: builder.mutation<NoteRecord, WorkOrderPatchArg>({
      query: ({ id, noteId, data }) => ({
        url: `/api/v1/core/work-orders/${id}/notes/${noteId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => writeInvalidatesTags(id),
    }),
  }),
})

export const {
  useLazyGetAllWorkOrderNotesQuery,
  useLazyGetWorkOrderNoteDetailsQuery,
  useLazyGetWorkOrderNoteHistoryQuery,
  useCreateWorkOrderNoteMutation,
  useUpdateWorkOrderNoteNewMutation,
  useDeleteWorkOrderNoteMutation,
  usePatchWorkOrderNoteMutation,
} = extendedApi
